import { FC, useCallback, useState } from "react";

import { LibraryItemDetailPageCoverImage_CoverImageFragment } from "../LibraryItemDetailPageContainer.generated";
import Text from "@src/lib/ui/Text";
import { Button } from "@src/lib/ui/Button";
import CoverImage from "@src/features/contentDetail/ui/CoverImage";
import { Input } from "@src/lib/ui/Input";
import { CoverImage as CoverImageType } from "@src/types.generated";
import SimpleRichTextEditor from "./SimpleRichTextEditor";
import UpdateCoverImageMenu from "@src/features/libraryItem/shared/UpdateCoverImageMenu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";

export type EditLibraryItemDialogData = {
  name: string;
  description: string;
  coverImage:
    | LibraryItemDetailPageCoverImage_CoverImageFragment
    | undefined
    | null;
};
type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  name: string;
  description: string;
  coverImage: CoverImageType | undefined | null;
  onSave: (data: EditLibraryItemDialogData) => void;
  disableCoverImageEditing?: boolean;
};
const labelWidthPx = 96;

const EditLibraryItemDialog: FC<Props> = (props) => {
  const [coverImage, setCoverImage] = useState<
    CoverImageType | undefined | null
  >(props.coverImage);
  const [name, setName] = useState(props.name);
  const [description, setDescription] = useState(props.description);
  const onSave = useCallback(() => {
    props.onSave({ name, description, coverImage });
    props.onOpenChange(false);
  }, [coverImage, description, name, props]);
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        className="max-w-[475px]"
        onInteractOutside={(e) => {
          // Pintura image editor appends its modal directly to document.body,
          // outside the Radix Dialog portal. Without this guard, Radix treats
          // clicks on the Pintura overlay as "outside" and closes the dialog.
          const target = (e.detail as { originalEvent?: Event })?.originalEvent
            ?.target as HTMLElement | undefined;
          if (target?.closest?.(".pintura")) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{props.title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-6">
          <div className="flex self-stretch">
            <Text type="P2" fontWeight="Medium" className="w-24">
              Cover
            </Text>
            <div className="relative flex aspect-video flex-1 items-center justify-center">
              <CoverImage
                editEnabled={false}
                style={{
                  flex: 1,
                  alignSelf: "stretch",
                }}
                coverImage={coverImage || undefined}
              />
              {!props.disableCoverImageEditing && (
                <UpdateCoverImageMenu
                  coverImage={coverImage}
                  setCoverImage={setCoverImage}
                />
              )}
            </div>
          </div>
          <div className="flex items-center self-stretch">
            <Text type="P2" fontWeight="Medium" className="w-24">
              Name
            </Text>
            <Input
              value={name}
              placeholder="New Hire Guide..."
              className="flex-1"
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          </div>
          <div className="flex self-stretch">
            <Text type="P2" fontWeight="Medium" className="w-24 pt-[9px]">
              Description
            </Text>
            <div style={{ width: `calc(100% - ${labelWidthPx}px)` }}>
              <SimpleRichTextEditor
                value={description}
                className="w-full"
                placeholder="This module will teach you..."
                onChange={(value) => {
                  setDescription(value);
                }}
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3 self-stretch">
            <Button variant="outline" onClick={() => props.onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={onSave}>Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditLibraryItemDialog;
